/** Property Drive — v1's Drive tab as a collapsible deal-drawer section.
 * Read-only in Phase A: category chips with counts, grid/list views, name
 * filter, thumbnails → open in Drive. v1's hardening ported: generation
 * guard (a stale load can never clobber a newer one), transient failures
 * show "waking up" + auto-retry (≤4 × 8s) and NEVER flip to the connect
 * state, per-address isolation (never show another deal's files). Uploads,
 * trash, and the connect flow are Phase B. */
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import {
  DRIVE_CATS_DISPO, DRIVE_CATS_FLIP, DRIVE_MSG, bigThumb, isDocFile, previewKind,
  type DriveFile, type DriveLoad,
} from "./api";
import { liveOn } from "../../lib/liveFire";

export interface DriveLoaders {
  files: (address: string, category: string, dispo: boolean) => Promise<DriveLoad>;
  categories: (address: string, dispo: boolean) => Promise<Record<string, number>>;
  /** live-fire (Phase B): resolves only on server-confirmed upload, else throws */
  upload: (address: string, category: string, dispo: boolean, file: File) => Promise<void>;
  /** the file's bytes through the backend — the in-app preview (2026-09-24);
   *  absent = fall back to Google's viewer */
  blob?: (fileId: string, signal?: AbortSignal) => Promise<{ blob: Blob; mime: string }>;
  /** phone PDF preview: page count, then each page as a JPEG */
  pdfPages?: (fileId: string, signal?: AbortSignal) => Promise<{ count: number; shown: number }>;
  pdfPage?: (fileId: string, n: number, width: number, signal?: AbortSignal) => Promise<Blob>;
}

/** Phones (and anything touch-first): Google's embedded viewer answers with
 *  an "open in the Drive app" page there, so every kind renders natively. */
export const isTouchFirst = (): boolean =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
  && (window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(max-width: 720px)").matches);

export function DriveSection({
  address,
  dispo,
  loaders,
  labels = {},
  onLabel,
}: {
  address: string;
  /** wholesale deals get the 2-category Drive (v1 _DRIVE_KIND==='dispo') */
  dispo: boolean;
  loaders: DriveLoaders;
  /** CRM-side names by Drive file id (Tom 2026-09-07: "name them on the drive
   *  in the app… not the name in the drive itself") */
  labels?: Record<string, string>;
  /** set/clear a CRM-side name; absent = read-only */
  onLabel?: (fileId: string, label: string | null) => void;
}) {
  const cats = dispo ? DRIVE_CATS_DISPO : DRIVE_CATS_FLIP;
  const [cat, setCat] = useState(cats[0] ?? "Documents");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [q, setQ] = useState("");
  const [files, setFiles] = useState<DriveFile[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [state, setState] = useState<"idle" | "loading" | "ok" | "waking" | "disconnected">("idle");
  const gen = useRef(0);
  const retries = useRef(0);
  const alive = useRef(true);
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState<string | null>(null);
  /** In-app preview (Tom 2026-07-30: "open a preview not take me to the
   *  drive"). Tiles preview here; Drive itself is behind each tile's ⋯. */
  const [preview, setPreview] = useState<DriveFile | null>(null);
  const [menuFor, setMenuFor] = useState<DriveFile | null>(null);
  /** the file being named in the CRM (⋯ → "Name in CRM") + the draft */
  const [naming, setNaming] = useState<DriveFile | null>(null);
  const [nameDraft, setNameDraft] = useState("");
  /** what the CRM shows for a file: its CRM name when set, else the Drive name */
  const shownName = (f: DriveFile): string => labels[f.id] || f.name;
  const startNaming = (f: DriveFile) => { setMenuFor(null); setNaming(f); setNameDraft(labels[f.id] ?? ""); };
  const commitName = () => {
    if (!naming || !onLabel) { setNaming(null); return; }
    onLabel(naming.id, nameDraft.trim() || null);
    setNaming(null);
  };
  /** Drive's embeddable viewer — same id the webViewLink carries. Desktop
   *  fallback for what browsers can't draw themselves (Word/Excel/video). */
  const previewSrc = (f: DriveFile): string => `https://drive.google.com/file/d/${encodeURIComponent(f.id)}/preview`;
  /** What the open preview is drawn from (2026-09-24). Photos and desktop
   *  PDFs: the file's bytes as an object URL. Phone PDFs: a stack of page
   *  images from the server. `err` = couldn't fetch → the thumbnail stands in. */
  const [blobUrl, setBlobUrl] = useState<{ id: string; url: string; mime: string } | null>(null);
  const [pages, setPages] = useState<{ id: string; count: number; shown: number; urls: string[] } | null>(null);
  const [blobErr, setBlobErr] = useState<string | null>(null);
  const [imgBroken, setImgBroken] = useState(false); // e.g. HEIC in Chrome → use Drive's JPEG thumbnail
  useEffect(() => {
    setBlobUrl(null); setPages(null); setBlobErr(null); setImgBroken(false);
    if (!preview) return;
    const kind = previewKind(preview);
    const touch = isTouchFirst();
    const ctl = new AbortController();
    const urls: string[] = [];
    const id = preview.id;
    const fail = (e: unknown) => { if (!ctl.signal.aborted) setBlobErr((e as { detail?: string })?.detail || (e as Error)?.message || "couldn't load the file"); };
    const isPdf = kind === "pdf" || kind === "gdoc";
    if (isPdf && touch && loaders.pdfPages && loaders.pdfPage) {
      // phone PDF: pages as images, shown as each one lands
      const pdfPage = loaders.pdfPage;
      const width = Math.min(2400, Math.round((window.innerWidth || 400) * Math.max(1, window.devicePixelRatio || 1)));
      loaders.pdfPages(id, ctl.signal).then(async ({ count, shown }) => {
        if (ctl.signal.aborted) return;
        setPages({ id, count, shown, urls: [] });
        for (let n = 1; n <= shown; n++) {
          const b = await pdfPage(id, n, width, ctl.signal);
          if (ctl.signal.aborted) return;
          urls.push(URL.createObjectURL(b));
          setPages({ id, count, shown, urls: [...urls] });
        }
      }).catch(fail);
    } else if (loaders.blob && (kind === "image" || isPdf)) {
      loaders.blob(id, ctl.signal).then(({ blob, mime }) => {
        if (ctl.signal.aborted) return;
        urls.push(URL.createObjectURL(blob));
        setBlobUrl({ id, url: urls[0]!, mime });
      }).catch(fail);
    }
    return () => { ctl.abort(); for (const u of urls) URL.revokeObjectURL(u); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.id]);
  const fileRef = useRef<HTMLInputElement>(null);
  const canUpload = liveOn("driveUpload");
  useEffect(() => () => {
    alive.current = false;
  }, []);

  // live-fire (Phase B) upload — now MULTI-file: pick as many as you want; they
  // upload sequentially through the same honest door (each file individually
  // server-confirmed; failures are listed by name and never faked into the
  // list). Single-flight (button locks) so nothing double-uploads; the Drive
  // list refreshes ONCE at the end with whatever the server actually accepted.
  const [prog, setProg] = useState<{ done: number; total: number } | null>(null);
  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same files after an error
    if (!picked.length || uploading) return;
    setUploading(true);
    setUpErr(null);
    setProg({ done: 0, total: picked.length });
    const failed: string[] = [];
    for (let i = 0; i < picked.length; i++) {
      const f = picked[i];
      if (!f) continue;
      try {
        await loaders.upload(address, cat, dispo, f);
      } catch (err) {
        failed.push(f.name + " — " + ((err as { detail?: string; message?: string })?.detail ?? (err as Error)?.message ?? DRIVE_MSG.uploadFail));
      }
      if (alive.current) setProg({ done: i + 1, total: picked.length });
    }
    if (!alive.current) return;
    if (failed.length) setUpErr((failed.length === picked.length ? "Upload failed: " : "Some files failed: ") + failed.join(" · "));
    load(cat); // reflect what Drive actually accepted (never an optimistic guess)
    setUploading(false);
    setProg(null);
  };

  const load = (category: string) => {
    const g = ++gen.current;
    setState("loading");
    void loaders.files(address, category, dispo).then((r) => {
      if (!alive.current || g !== gen.current) return; // a newer load took over
      if (r.kind === "ok") {
        retries.current = 0;
        setFiles(r.files);
        setState("ok");
      } else if (r.kind === "disconnected") {
        setState("disconnected");
      } else {
        setState("waking");
        if (retries.current++ < 4) {
          setTimeout(() => {
            if (alive.current && g === gen.current) load(category);
          }, 8000);
        }
      }
    });
    void loaders.categories(address, dispo).then((c) => {
      if (alive.current && g === gen.current && Object.keys(c).length) setCounts(c);
    });
  };

  // Always-open now: fire the initial load once on mount (was the toggle-open trigger).
  useEffect(() => {
    if (state === "idle") load(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickCat = (c: string) => {
    setCat(c);
    setFiles(null);
    load(c);
  };

  const shown = (files ?? []).filter((f) => !q || f.name.toLowerCase().includes(q.toLowerCase()) || (labels[f.id] ?? "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>📁 Property Drive</span>
        <span className="sub">
          {state === "ok" && files ? `${files.length} in ${cat} · ` : ""}
        </span>
      </div>
        <div className="secbody drivesec">
          <div className="drivehead">
            <span className="sub">{state === "loading" ? DRIVE_MSG.syncing : DRIVE_MSG.synced} · {(address.split(",")[0] ?? "").trim()}</span>
            <div className="mailacts">
              <button className={"btn slim" + (view === "grid" ? " primary" : "")} onClick={() => setView("grid")} title="Grid">▦</button>
              <button className={"btn slim" + (view === "list" ? " primary" : "")} onClick={() => setView("list")} title="List">☰</button>
              <button className="btn slim" onClick={() => load(cat)} title="Refresh from Drive">⟳</button>
              {canUpload ? (
                <>
                  <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={(e) => void onPick(e)} />
                  <button className="btn slim primary" disabled={uploading} onClick={() => fileRef.current?.click()} title={`Upload to ${cat} — pick as many files as you want`}>
                    {uploading
                      ? prog && prog.total > 1
                        ? `Uploading ${Math.min(prog.done + 1, prog.total)}/${prog.total}…`
                        : DRIVE_MSG.uploading
                      : "+ Upload"}
                  </button>
                </>
              ) : (
                <button className="btn slim" disabled title={DRIVE_MSG.uploadPhaseB}>+ Upload</button>
              )}
            </div>
          </div>
          {upErr ? <div className="mailsenderr">{upErr}</div> : null}
          <div className="drivecats">
            {cats.map((c) => (
              <button key={c} className={"pillbtn" + (cat === c ? " on-active" : " off-plain")} onClick={() => pickCat(c)}>
                {c}
                {counts[c] ? <em> {counts[c]}</em> : null}
              </button>
            ))}
          </div>
          <input className="drivesearch" placeholder="Search files…" value={q} onChange={(e) => setQ(e.target.value)} />
          {state === "disconnected" ? (
            <div className="arvempty">
              <div className="sub">Files for {(address.split(",")[0] ?? "").trim()} live in your Google Drive.</div>
              <button className="btn" disabled title={DRIVE_MSG.connectPhaseB}>Connect Google Drive — Phase B</button>
            </div>
          ) : state === "waking" ? (
            <div className="arvempty">
              <div className="sub">Backend waking up…</div>
              <span className="sub dim">{DRIVE_MSG.waking}</span>
              <button className="btn" onClick={() => { retries.current = 0; load(cat); }}>⟳ Retry now</button>
            </div>
          ) : state === "loading" && !shown.length ? (
            <div className="arvempty"><span className="sub">{DRIVE_MSG.loading}</span></div>
          ) : shown.length ? (
            view === "grid" ? (
              <div className="drivegrid">
                {/* buttons, not links (Tom 2026-07-30): a tap previews IN the
                    app; Google Drive itself moved behind each tile's ⋯ */}
                {shown.map((f) => (
                  <button key={f.id} type="button" className={"drivecard" + (labels[f.id] ? " named" : "")} title={labels[f.id] ? `${labels[f.id]} — Drive: ${f.name}` : f.name} onClick={() => setPreview(f)}>
                    <span className="drivemore" role="button" aria-label={`Actions for ${f.name}`} onClick={(e) => { e.stopPropagation(); setMenuFor(f); }}>⋯</span>
                    <div className="drivethumb">
                      {f.thumbnailLink ? (
                        <img src={f.thumbnailLink} referrerPolicy="no-referrer" loading="lazy" alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                      ) : (
                        <span className="driveicon">{isDocFile(f.name) ? "📄" : "🖼"}</span>
                      )}
                    </div>
                    <div className="drivename" title={shownName(f)}>{shownName(f)}</div>
                    {labels[f.id] ? <div className="drivealt" title={f.name}>{f.name}</div> : null}
                    <div className="drivemeta">{f.size}{f.size && f.date ? " · " : ""}{f.date}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="drivelist">
                {shown.map((f) => (
                  <button key={f.id} type="button" className="driverow" onClick={() => setPreview(f)}>
                    <span className="driveicon">{isDocFile(f.name) ? "📄" : "🖼"}</span>
                    <span className="drivename" title={labels[f.id] ? `${labels[f.id]} — Drive: ${f.name}` : f.name}>{shownName(f)}{labels[f.id] ? <span className="drivealt inline"> · {f.name}</span> : null}</span>
                    <span className="drivemeta">{f.size}{f.size && f.date ? " · " : ""}{f.date}{f.by ? " · " + f.by : ""}</span>
                    <span className="drivemore inline" role="button" aria-label={`Actions for ${f.name}`} onClick={(e) => { e.stopPropagation(); setMenuFor(f); }}>⋯</span>
                  </button>
                ))}
              </div>
            )
          ) : state === "ok" ? (
            <div className="arvempty">
              <div className="sub">{DRIVE_MSG.emptyCat(cat)}</div>
              <span className="sub dim">Uploads land here and in Google Drive — from v1 until the live-fire session.</span>
            </div>
          ) : null}
        </div>

      {/* in-app preview (2026-09-24: no longer Google's viewer for photos and
          PDFs — it answered phones with "open in the Drive app"). Photos are
          an <img> of the real bytes (Drive's big thumbnail until they land),
          PDFs the browser's own viewer on desktop and a tap-to-open on
          phones; Word/Excel/video keep Google's viewer on desktop and show
          their first-page render + Open in Drive on phones. */}
      {preview ? (() => {
        const kind = previewKind(preview);
        const touch = isTouchFirst();
        const isPdf = kind === "pdf" || kind === "gdoc";
        const thumb = bigThumb(preview);
        const mine = blobUrl && blobUrl.id === preview.id ? blobUrl : null;
        const pg = pages && pages.id === preview.id ? pages : null;
        const driveLink = preview.webViewLink
          ? <a className="btn" href={preview.webViewLink} target="_blank" rel="noreferrer">↗ Open in Google Drive</a> : null;
        let body: ReactNode;
        if (kind === "image" && loaders.blob) {
          const src = mine && !imgBroken ? mine.url : thumb;
          body = src
            ? <div className="drivepvbody"><img className="drivepvimg" src={src} alt={preview.name} referrerPolicy="no-referrer" onError={() => setImgBroken(true)} />{!mine && !blobErr ? <span className="drivepvstate">loading full size…</span> : null}</div>
            : <div className="drivepvbody"><span className="sub">{blobErr ? `Couldn't load this photo (${blobErr}).` : "Loading photo…"}</span></div>;
        } else if (isPdf && touch && loaders.pdfPages) {
          // phone PDF: the pages, top to bottom, as they arrive
          body = (
            <div className="drivepvbody drivepvpages">
              {pg ? pg.urls.map((u, i) => <img key={i} className="drivepvpage" src={u} alt={`${preview.name} — page ${i + 1}`} />) : null}
              {blobErr ? (
                <div className="drivepvopen">{thumb ? <img className="drivepvimg" src={thumb} alt="" referrerPolicy="no-referrer" /> : null}<span className="caerr">Couldn't render this PDF ({blobErr}).</span>{driveLink}</div>
              ) : !pg || pg.urls.length < pg.shown ? (
                <span className="drivepvstate">{pg ? `page ${pg.urls.length + 1} of ${pg.shown}…` : "Opening PDF…"}</span>
              ) : pg.count > pg.shown ? (
                <div className="drivepvopen"><span className="sub">First {pg.shown} of {pg.count} pages.</span>{driveLink}</div>
              ) : null}
            </div>
          );
        } else if (isPdf && loaders.blob && !touch) {
          // desktop PDF: the browser's own viewer on the real bytes
          body = mine
            ? <iframe className="drivepvframe" src={mine.url} title={preview.name} />
            : <div className="drivepvbody"><span className="sub">{blobErr ? `Couldn't load this PDF (${blobErr}).` : "Loading PDF…"}</span>{blobErr ? driveLink : null}</div>;
        } else if (!touch) {
          // desktop Word/Excel/video: Google's viewer still draws these best
          body = <iframe className="drivepvframe" src={previewSrc(preview)} title={preview.name} allow="autoplay" />;
        } else {
          // phone Word/Excel/video: first-page render + the one tap to Drive
          body = (
            <div className="drivepvbody drivepvdoc">
              {thumb ? <img className="drivepvimg" src={thumb} alt={preview.name} referrerPolicy="no-referrer" /> : <span className="driveicon big">{isDocFile(preview.name) ? "📄" : "🎞"}</span>}
              <div className="drivepvopen">{driveLink}<span className="sub dim">{thumb ? "first page shown · " : ""}this file type opens in Drive</span></div>
            </div>
          );
        }
        return (
          <div className="cmpmodalscrim" onClick={() => setPreview(null)}>
            <div className="cmpmodal drivepreview" onClick={(e) => e.stopPropagation()}>
              <div className="drivepvhead">
                <span className="drivepvname" title={preview.name}>{shownName(preview)}{labels[preview.id] ? <span className="drivealt inline"> · {preview.name}</span> : null}</span>
                {onLabel ? <button className="btn slim" onClick={() => { setPreview(null); startNaming(preview); }}>✏ Name in CRM</button> : null}
                {preview.webViewLink ? (
                  <a className="btn slim" href={preview.webViewLink} target="_blank" rel="noreferrer">↗ Open in Drive</a>
                ) : null}
                <button className="close" onClick={() => setPreview(null)}>×</button>
              </div>
              {body}
            </div>
          </div>
        );
      })() : null}

      {/* tile ⋯ — the one action that used to BE the tap */}
      {menuFor ? (
        <div className="cmpmodalscrim" onClick={() => setMenuFor(null)}>
          <div className="cmpmodal drivemenu" onClick={(e) => e.stopPropagation()}>
            <div className="drivepvhead">
              <span className="drivepvname" title={menuFor.name}>{menuFor.name}</span>
              <button className="close" onClick={() => setMenuFor(null)}>×</button>
            </div>
            <button className="drivemenuitem" onClick={() => { setMenuFor(null); setPreview(menuFor); }}>👁 Preview</button>
            {onLabel ? (
              <button className="drivemenuitem" onClick={() => startNaming(menuFor)}>
                ✏ {labels[menuFor.id] ? "Rename in CRM" : "Name in CRM"}
                <span className="sub dim"> — only what the CRM shows; the Drive file keeps its name</span>
              </button>
            ) : null}
            {menuFor.webViewLink ? (
              <a className="drivemenuitem" href={menuFor.webViewLink} target="_blank" rel="noreferrer" onClick={() => setMenuFor(null)}>↗ Open in Google Drive</a>
            ) : (
              <div className="sub dim">No Drive link on this file.</div>
            )}
          </div>
        </div>
      ) : null}

      {/* name a file IN THE CRM (Tom 2026-09-07) — Drive is never touched */}
      {naming ? (
        <div className="cmpmodalscrim" onClick={() => setNaming(null)}>
          <div className="cmpmodal drivemenu drivenaming" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Name this file in the CRM">
            <div className="drivepvhead">
              <span className="drivepvname">Name in CRM</span>
              <button className="close" onClick={() => setNaming(null)}>×</button>
            </div>
            <div className="sub">Drive file: <b>{naming.name}</b> (unchanged)</div>
            <input
              className="moneyinput drivenameinput"
              autoFocus
              value={nameDraft}
              placeholder="e.g. Insurance certificate, W-9 — JR Roofing, Closing statement"
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") commitName(); if (e.key === "Escape") setNaming(null); }}
              aria-label="Name in CRM"
            />
            <div className="modalacts">
              <button className="btn slim primary" onClick={commitName}>Save</button>
              {labels[naming.id] ? <button className="btn slim" onClick={() => { setNameDraft(""); onLabel?.(naming.id, null); setNaming(null); }}>Clear (show Drive name)</button> : null}
              <button className="btn slim" onClick={() => setNaming(null)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
